import React, { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../../components/BaseUI/Table";
import Lozenge from "../../../../components/BaseUI/Lozenge";
import Pagination from "../../../../components/BaseUI/Pagination";
import { FiDownload } from "react-icons/fi";
import Button from "../../../../components/BaseUI/Button";
import {
  KpiCard,
  SectionSpinner,
  SectionEmpty,
  exportXLSX,
  salesMonthLabelTr,
} from "../shared/utils";
import { useSalesReport } from "./useSalesReport";
import { useRevenueTables } from "./useRevenueTables";
import SalesReportShell from "./SalesReportShell";

const fmtTL = (v) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
      }).format(v);

const PAYMENT_METHOD_TR = {
  cash: "Nakit",
  credit_card: "Kredi Kartı",
  bank_transfer: "Banka Havalesi",
};

const PAYMENT_STATUS_TR = {
  pending: "Beklemede",
  paid: "Ödendi",
  partial: "Kısmi Ödeme",
  refunded: "İade",
};

const PAYMENT_STATUS_APPEARANCE = {
  pending: "warning-subtle",
  paid: "success-subtle",
  partial: "inprogress-subtle",
  refunded: "danger-subtle",
};

const SalesRevenueReport = () => {
  useTitle("Rovion MES - Gelir Analizi");
  const {
    years,
    year,
    handleYearChange,
    salesData,
    loading: salesLoading,
    toasts,
    dismissToast,
  } = useSalesReport();

  const [month, setMonth] = useState("all");

  useEffect(() => {
    setMonth("all");
  }, [year]);

  const monthOptions = useMemo(() => {
    const opts = [{ value: "all", label: "Tüm Yıl" }];
    (salesData?.monthlySales || []).forEach((m, i) => {
      const hasData =
        m.count > 0 ||
        m.deviceCount > 0 ||
        (m.models || []).some((mod) => (mod.count || 0) > 0);
      if (hasData) {
        opts.push({
          value: String(i),
          label: salesMonthLabelTr(i, m.year || year),
        });
      }
    });
    return opts;
  }, [salesData, year]);

  const periodLabel =
    month === "all"
      ? String(year)
      : monthOptions.find((o) => o.value === month)?.label || String(year);

  const {
    revenueData,
    summaryLoading,
    methodRows,
    methodPg,
    methodLoading,
    handleMethodPageChange,
    statusRows,
    statusPg,
    statusLoading,
    handleStatusPageChange,
    partnerRows,
    partnersPg,
    partnersLoading,
    handlePartnersPageChange,
    modelRows,
    modelsPg,
    modelsLoading,
    handleModelsPageChange,
    fetchAllModelsForExport,
  } = useRevenueTables(year, month);

  const loading = salesLoading || summaryLoading;

  const content = () => {
    if (loading && !revenueData) return <SectionSpinner />;
    if (!revenueData) return <SectionEmpty msg="Gelir verisi bulunamadı." />;

    const {
      byPaymentMethod = {},
      byPaymentStatus = {},
      totalRevenue,
      averageOrderValue,
      totalSales,
    } = revenueData;

    const methodEntries = Object.entries(byPaymentMethod).filter(
      ([, v]) => v.count > 0,
    );
    const statusEntries = Object.entries(byPaymentStatus).filter(
      ([, v]) => v.count > 0,
    );

    const methodDonut = {
      series: methodEntries.map(([, v]) => v.revenue),
      options: {
        chart: { type: "donut", fontFamily: "inherit" },
        labels: methodEntries.map(([k]) => PAYMENT_METHOD_TR[k] || k),
        legend: { position: "bottom" },
        tooltip: { y: { formatter: fmtTL } },
        colors: ["#2ecc71", "#4472C4", "#e67e22"],
      },
    };

    const statusDonut = {
      series: statusEntries.map(([, v]) => v.revenue),
      options: {
        chart: { type: "donut", fontFamily: "inherit" },
        labels: statusEntries.map(([k]) => PAYMENT_STATUS_TR[k] || k),
        legend: { position: "bottom" },
        tooltip: { y: { formatter: fmtTL } },
        colors: ["#f39c12", "#2ecc71", "#3498db", "#e74c3c"],
      },
    };

    return (
      <>
        <div className="rp-revenue-grid-2">
          {methodEntries.length > 0 && (
            <CardContainer title="Ödeme Yöntem Dağılımı" fillHeight>
              <ReactApexChart
                options={methodDonut.options}
                series={methodDonut.series}
                type="donut"
                height={280}
              />
            </CardContainer>
          )}
          {statusEntries.length > 0 && (
            <CardContainer title="Ödeme Durum Dağılımı" fillHeight>
              <ReactApexChart
                options={statusDonut.options}
                series={statusDonut.series}
                type="donut"
                height={280}
              />
            </CardContainer>
          )}
        </div>

        <div className="rp-revenue-grid-2 mb-3">
          {(methodPg.total > 0 || methodLoading) && (
            <CardContainer title="Ödeme Yöntemi Detayı" fillHeight>
              <Table
                columns={["Yöntem", "İşlem", "Gelir"].map((name, id) => ({
                  name,
                  id,
                  sortable: false,
                }))}
                search={false}
                hideFooter
                loading={methodLoading}
              >
                {methodRows.map((row) => (
                  <Tr key={row.key}>
                    <Td>{PAYMENT_METHOD_TR[row.key] || row.key}</Td>
                    <Td>{row.count}</Td>
                    <Td>{fmtTL(row.revenue)}</Td>
                  </Tr>
                ))}
              </Table>
              <Pagination
                totalItems={methodPg.total}
                totalPerPage={methodPg.pageSize}
                currentPage={methodPg.page}
                onPageChange={handleMethodPageChange}
              />
            </CardContainer>
          )}
          {(statusPg.total > 0 || statusLoading) && (
            <CardContainer title="Ödeme Durumu Detayı" fillHeight>
              <Table
                columns={["Durum", "İşlem", "Gelir"].map((name, id) => ({
                  name,
                  id,
                  sortable: false,
                }))}
                search={false}
                hideFooter
                loading={statusLoading}
              >
                {statusRows.map((row) => (
                  <Tr key={row.key}>
                    <Td>
                      <Lozenge
                        appearance={
                          PAYMENT_STATUS_APPEARANCE[row.key] || "default-subtle"
                        }
                      >
                        {PAYMENT_STATUS_TR[row.key] || row.key}
                      </Lozenge>
                    </Td>
                    <Td>{row.count}</Td>
                    <Td>{fmtTL(row.revenue)}</Td>
                  </Tr>
                ))}
              </Table>
              <Pagination
                totalItems={statusPg.total}
                totalPerPage={statusPg.pageSize}
                currentPage={statusPg.page}
                onPageChange={handleStatusPageChange}
              />
            </CardContainer>
          )}
        </div>

        {(partnersPg.total > 0 || partnersLoading) && (
          <CardContainer
            className="mb-3"
            title="En İyi Sipariş Ortakları"
            fillHeight
          >
            <Table
              columns={["#", "Ortak", "Tür", "Sipariş", "Ürün", "Ciro"].map(
                (name, id) => ({ name, id, sortable: false }),
              )}
              search={false}
              hideFooter
              loading={partnersLoading}
            >
              {partnerRows.map((p, i) => (
                <Tr key={p._id}>
                  <Td>{(partnersPg.page - 1) * partnersPg.pageSize + i + 1}</Td>
                  <Td>{p.name}</Td>
                  <Td>
                    <Lozenge
                      appearance={
                        p.type === "dealer"
                          ? "inprogress-subtle"
                          : "default-subtle"
                      }
                    >
                      {p.type === "dealer" ? "Bayi" : "Firma"}
                    </Lozenge>
                  </Td>
                  <Td>{p.stats.salesCount}</Td>
                  <Td>{p.stats.deviceCount}</Td>
                  <Td>{fmtTL(p.stats.revenue)}</Td>
                </Tr>
              ))}
            </Table>
            <Pagination
              totalItems={partnersPg.total}
              totalPerPage={partnersPg.pageSize}
              currentPage={partnersPg.page}
              onPageChange={handlePartnersPageChange}
            />
          </CardContainer>
        )}

        {(modelsPg.total > 0 || modelsLoading) && (
          <CardContainer fillHeight>
            <div className="d-flex justify-content-between align-items-center mb-2 p-2">
              <h5 className="mb-0">Model Detay Tablosu ({periodLabel})</h5>
              <Button
                size="sm"
                appearance="subtle"
                onClick={async () => {
                  const rows = await fetchAllModelsForExport();
                  exportXLSX(
                    rows.map((r) => ({
                      Model: r.name,
                      "Sipariş Adedi": r.count,
                      "Ciro (₺)": r.revenue,
                    })),
                    `model-satis-${periodLabel}`,
                  );
                }}
              >
                <FiDownload size={14} className="me-1" /> Excel
              </Button>
            </div>
            <Table
              columns={[
                "#",
                "Model",
                "Sipariş Adedi",
                "Ciro",
                "Ort. Fiyat",
              ].map((name, id) => ({ name, id, sortable: false }))}
              search={false}
              hideFooter
              loading={modelsLoading}
            >
              {modelRows.map((row, i) => (
                <Tr key={row.name}>
                  <Td>{(modelsPg.page - 1) * modelsPg.pageSize + i + 1}</Td>
                  <Td>{row.name}</Td>
                  <Td>{row.count}</Td>
                  <Td>{fmtTL(row.revenue)}</Td>
                  <Td>
                    {row.count > 0
                      ? fmtTL(Math.round(row.revenue / row.count))
                      : "—"}
                  </Td>
                </Tr>
              ))}
            </Table>
            <Pagination
              totalItems={modelsPg.total}
              totalPerPage={modelsPg.pageSize}
              currentPage={modelsPg.page}
              onPageChange={handleModelsPageChange}
            />
          </CardContainer>
        )}
      </>
    );
  };

  return (
    <SalesReportShell
      title="Gelir Analizi"
      year={year}
      years={years}
      onYearChange={handleYearChange}
      showMonthFilter
      month={month}
      monthOptions={monthOptions}
      onMonthChange={setMonth}
      toasts={toasts}
      dismissToast={dismissToast}
    >
      {content()}
    </SalesReportShell>
  );
};

export default withAuth(SalesRevenueReport);
